import { useNavigate } from 'react-router-dom'
import { useRecoilValue } from 'recoil'
import { URLSearchParams } from 'url'

import { $tenant } from '../../state/auth'
import { useUrlContext } from '../../state/url-context'

export interface NavigationHook {
  (
    url: string,
    sitemap?: string,
    additionalCtx?: Record<string, string>,
    query?: URLSearchParams,
    state?: Record<string, object>,
  ): void
}

/**
 * The `useNavigation` function is a hook that returns a navigation function which takes
 * a URL and an optional sitemap parameter, and navigates to the specified URL with the serialized
 * context and sitemap.
 */
export function useNavigation(): NavigationHook {
  const { context, serialize, update } = useUrlContext()
  const tenant = useRecoilValue($tenant)
  const navigate = useNavigate()
  const { pathname, search } = window.location

  const result = ((
    url: string,
    sitemap?: string,
    additionalCtx?: Record<string, string>,
    query?: URLSearchParams,
    state?: Record<string, object>,
  ) => {
    // Keep the context aligned with the current URL
    // This is necessary to ensure that the context is always up-to-date with the URL
    // As with the filter functionality we are updating the url using the history replaceState
    // That is needed because updating the context will trigger a re-render of the component
    // Causing flickering in the UI
    const updated = update(pathname + search)

    const updatedCtx = { ...updated, ...additionalCtx }
    const ctx = serialize({
      ...updatedCtx,
    })
    navigate(`/${tenant}/${sitemap ?? context.sitemap}/${url}?s=${ctx}${query ? `&${query.toString()}` : ''}`, {
      state,
    })
  }) as NavigationHook

  return result
}
